import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext.js';
import NhsAuthHelper from './NhsAuthHelper.js';
import './LoginView.css';

const LoginViewController = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isHovering, setIsHovering] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  // Set page title when component mounts
  useEffect(() => {
    document.title = 'MedPrompt - Login';
  }, []);

  // Check if already authenticated on component mount
  useEffect(() => {
    // Clear any auth redirect flag to prevent infinite reloads
    sessionStorage.removeItem('auth_redirect');
    
    if (user) {
      // Navigate to the originally requested page or default to home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    
    const normalizedEmail = email.toLowerCase();

    try {
      setLoading(true);
      setError('');
      
      const result = await login(normalizedEmail, password);
      
      // If login requires verification, redirect to verification page
      if (result && result.requiresVerification) {
        navigate('/verify', { 
          state: { 
            email: normalizedEmail,
            from: location.state?.from || { pathname: '/' }
          } 
        });
      }
      // If no verification required, the useEffect will handle navigation when user state updates
      
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed: ' + (error.message || 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handlePasswordReset = () => {
    navigate('/signup', { 
      state: { 
        isReset: true 
      } 
    });
  };

  
  const handleAlternativeLogin = () => {
    NhsAuthHelper.startAuth({ purpose: 'login' });
  };

  const getSvgSource = () => {
    if (isActive) {
      return '/CIS2_LogInWith_Original-Active.svg';
    } else if (isHovering) {
      return '/CIS2_LogInWith_Original-Hover.svg';
    } else {
      return '/CIS2_LogInWith_Original.svg';
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-header">
          <div className="login-brand">
            <a
              href="https://medgenixtech.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="login-brand-company"
            >
              <img
                src="/medgenix-tech.png"
                alt=""
                className="login-brand-logo"
              />
              <span>MedGenix Tech</span>
            </a>
            <hr className="login-brand-rule" />
            <h1 className="login-brand-product">MedPrompt</h1>
          </div>
          <p>Your AI-powered clinical workflow platform</p>
        </div>
        
        {error && <div className="login-error">{error}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={loading}
              required
            />
          </div>
          
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Log in with my password'}
          </button>
          
          {process.env.REACT_APP_NHS_AUTH_DISABLED !== 'true' && (
            <div className="alternative-login">
              <img 
                src={getSvgSource()}
                alt="Alternative Login" 
                onClick={handleAlternativeLogin}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onMouseDown={() => setIsActive(true)}
                onMouseUp={() => setIsActive(false)}
                className="alternative-login-svg"
              />
            </div>
          )}
          
          {process.env.REACT_APP_NHS_AUTH_DISABLED !== 'true' && (
            <button type="button" className="text-button" onClick={handleSignUp}>
              Received an invite email? Sign up here
            </button>
          )}
          
          {process.env.REACT_APP_NHS_AUTH_DISABLED !== 'true' && (
            <button type="button" className="text-button" onClick={handlePasswordReset}>
              Received a password reset email? Reset it here
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginViewController;